import { API_HOST } from '@/constants/config';

// SSR-safe prompts API client used by `generateStaticParams` and other
// build-time SEO paths (/prompts, /prompts/[slug]).
// Mirrors `lib/toolApi.js` — direct fetch (no auth, no Zustand).

const toQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    searchParams.set(key, String(value));
  });
  const query = searchParams.toString();
  return query ? `?${query}` : '';
};

const fetchPrompts = async (path, params = {}) => {
  const url = `${API_HOST}${path}${toQueryString(params)}`;
  let response;

  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Platform': 'web',
      },
    });
  } catch {
    throw new Error('Network error — unable to reach the prompts service.');
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error(
      response.ok
        ? 'Prompts service returned an unexpected response.'
        : `Prompts service returned ${response.status}.`,
    );
  }

  const data = await response.json();
  if (!response.ok || data?.error) {
    throw new Error(data?.message || 'Failed to fetch prompts data');
  }

  return data;
};

const PROMPTS_PAGE_SIZE = 200;

export const promptApi = {
  listPrompts: async ({ category, tag, featured, inputType, outputType, limit, skip } = {}) => {
    const data = await fetchPrompts('/json/prompts', {
      category, tag, featured, inputType, outputType, limit, skip,
    });
    return {
      prompts: data.prompts || [],
      pagination: data.pagination || null,
      total: data.total ?? null,
    };
  },

  /** Paginate through every active prompt (build-time SSG / sitemap helpers). */
  listAllPrompts: async (params = {}) => {
    const all = [];
    let skip = 0;

    while (true) {
      const data = await fetchPrompts('/json/prompts', {
        ...params,
        limit: PROMPTS_PAGE_SIZE,
        skip,
      });
      const batch = data.prompts || [];
      all.push(...batch);

      const total = data.total ?? data.pagination?.total ?? all.length;
      if (!batch.length || all.length >= total) break;

      skip += batch.length;
      if (skip > 50000) break;
    }

    return all;
  },

  getPromptBySlug: async (slug) => {
    if (!slug) return null;
    const data = await fetchPrompts(`/json/prompts/${encodeURIComponent(slug)}`);
    return data.prompt || null;
  },

  listCategories: async () => {
    const data = await fetchPrompts('/json/prompt-categories');
    return {
      categories: data.promptCategories || [],
    };
  },
};
